import React, { useState, useRef, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { DropdownItem } from './Dropdown'
import Logo from './Logo'
import { ChevronDown, FolderKanban, Home, Search, Shield } from 'lucide-react'
import { useUser } from '../../contexts/UserContext'

export interface UnifiedTopNavProps {
  inProgramContext?: boolean
  programId?: string
  programName?: string
  programStatus?: string
  programStatusDisplay?: string
  onLogout: () => void
  userName?: string
  userOfficeName?: string
}

const UnifiedTopNav: React.FC<UnifiedTopNavProps> = ({
  inProgramContext = false,
  programId,
  programName,
  programStatus: _programStatus,
  programStatusDisplay: _programStatusDisplay,
  onLogout,
  userName = 'User',
  userOfficeName = 'Office'
}) => {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const [isProgramMenuOpen, setIsProgramMenuOpen] = useState(false)
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)
  const programMenuRef = useRef<HTMLDivElement>(null)
  const mobileNavRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { user } = useUser()

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false)
      }
      if (programMenuRef.current && !programMenuRef.current.contains(event.target as Node)) {
        setIsProgramMenuOpen(false)
      }
      if (mobileNavRef.current && !mobileNavRef.current.contains(event.target as Node)) {
        setIsMobileNavOpen(false)
      }
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsUserMenuOpen(false)
        setIsProgramMenuOpen(false)
        setIsMobileNavOpen(false)
      }
    }

    if (isUserMenuOpen || isProgramMenuOpen || isMobileNavOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleEscape)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isUserMenuOpen, isProgramMenuOpen, isMobileNavOpen])

  // Dropdown items for program context
  const programDropdownItems: DropdownItem[] = [
    { label: 'Scorecards', to: `/programs/${programId}/setup/scorecards` },
    { label: 'Interviews', to: `/programs/${programId}/interviews` },
    { label: 'Team', to: `/programs/${programId}/setup/team` },
    { label: 'Process', to: `/programs/${programId}/setup/process` },
    { label: 'Settings', to: `/programs/${programId}/settings` },
  ]

  const marketDropdownItems: DropdownItem[] = [
    { label: 'Analysis of Alternatives', to: `/programs/${programId}/solutions` },
    { label: 'Market Search', to: `/programs/${programId}/market` },
    { label: 'Vendor Shortlist', to: `/programs/${programId}/vendors` },
    { label: 'Cost Register', to: `/programs/${programId}/cost-register` },
  ]

  const documentsDropdownItems: DropdownItem[] = [
    { label: 'All Documents', to: `/programs/${programId}/setup/documents` },
  ]

  // Organized sections for collapsed menu
  const collapsedMenuSections = [
    { title: 'Program', items: programDropdownItems },
    { title: 'Market', items: marketDropdownItems },
    { title: 'Documents', items: documentsDropdownItems },
  ]

  return (
    <nav className="relative z-40 h-16 bg-card border-b border-border shadow-xs">
      <div className="h-full pl-0 pr-2 flex items-center justify-between">
        {/* Left section: Logo and main navigation */}
        <div className="flex items-center gap-0">
          {/* Mobile: logo opens Home/Programs/Market menu */}
          <div className="relative flex sm:hidden" ref={mobileNavRef}>
            <button
              type="button"
              onClick={() => setIsMobileNavOpen(!isMobileNavOpen)}
              className="flex items-center flex-shrink-0 rounded-md hover:bg-primary/10 transition-colors"
              aria-label="Open menu"
              aria-expanded={isMobileNavOpen}
            >
              <Logo className="h-16 w-[220px] flex-shrink-0 overflow-hidden" />
            </button>
            {isMobileNavOpen && (
              <div className="absolute top-full left-0 mt-1 w-48 bg-popover border border-border rounded-md shadow-lg z-50 py-1">
                <Link
                  to="/home"
                  onClick={() => setIsMobileNavOpen(false)}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-popover-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                >
                  <Home className="w-4 h-4" />
                  Home
                </Link>
                <Link
                  to="/programs"
                  onClick={() => setIsMobileNavOpen(false)}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-popover-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                >
                  <FolderKanban className="w-4 h-4" />
                  Programs
                </Link>
                <Link
                  to="/market"
                  onClick={() => setIsMobileNavOpen(false)}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-popover-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                >
                  <Search className="w-4 h-4" />
                  Market
                </Link>
              </div>
            )}
          </div>

          {/* Desktop: logo links to home */}
          <Link to="/home" className="hidden sm:flex items-center flex-shrink-0">
            <Logo className="h-16 w-[220px] flex-shrink-0 overflow-hidden" />
          </Link>

          <div className="hidden sm:flex items-center gap-1">
            <Link
              to="/home"
              className="flex flex-col items-center gap-0.5 px-2 py-1 rounded-md hover:bg-primary/10 transition-all group"
            >
              <Home className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
              <span className="text-[10px] font-medium text-foreground group-hover:text-primary transition-colors leading-tight">
                Home
              </span>
            </Link>
            
            <Link
              to="/programs"
              className="flex flex-col items-center gap-0.5 px-2 py-1 rounded-md hover:bg-primary/10 transition-all group"
            >
              <FolderKanban className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
              <span className="text-[10px] font-medium text-foreground group-hover:text-primary transition-colors leading-tight">
                Programs
              </span>
            </Link>

            <Link
              to="/market"
              className="flex flex-col items-center gap-0.5 px-2 py-1 rounded-md hover:bg-primary/10 transition-all group"
            >
              <Search className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
              <span className="text-[10px] font-medium text-foreground group-hover:text-primary transition-colors leading-tight">
                Market
              </span>
            </Link>
          </div>
        </div>

        {/* Center section: Program context (only shown when in program) */}
        {inProgramContext && programId && (
          <div className="flex items-center gap-4 min-w-0 flex-1 justify-center overflow-visible">
            {/* Program name and status - clickable on small screens */}
            <div className="relative min-w-0 max-w-full" ref={programMenuRef}>
              <button
                type="button"
                onClick={() => setIsProgramMenuOpen(!isProgramMenuOpen)}
                className="flex items-center gap-3 px-4 py-1.5 bg-muted rounded-md hover:bg-muted/80 transition-colors min-w-0 max-w-full"
              >
                <span className="text-sm font-semibold text-foreground truncate block min-w-0">
                  {programName || 'Program'}
                </span>
                <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${isProgramMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Collapsed menu with sections for smaller screens (includes user/account at bottom) */}
              {isProgramMenuOpen && (
                <div className="absolute top-full left-0 mt-1 w-64 bg-popover border border-border rounded-md shadow-lg z-50 max-h-[32rem] overflow-y-auto">
                  <div className="py-1">
                    {collapsedMenuSections.map((section, sectionIndex) => (
                      <div key={section.title}>
                        {sectionIndex > 0 && <div className="border-t border-border my-1" />}
                        <div className="px-3 py-2">
                          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                            {section.title}
                          </div>
                          {section.items.map((item, itemIndex) => (
                            <Link
                              key={itemIndex}
                              to={item.to}
                              onClick={() => {
                                setIsProgramMenuOpen(false)
                                item.onClick?.()
                              }}
                              className="block px-2 py-1.5 text-sm text-popover-foreground hover:bg-accent hover:text-accent-foreground rounded transition-colors"
                            >
                              {item.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    ))}
                    {/* User / Account section at bottom */}
                    <div className="border-t border-border my-1" />
                    <div className="px-3 py-2">
                      <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                        Account
                      </div>
                      <div className="px-2 py-1.5 text-xs text-muted-foreground italic">
                        <div className="font-medium text-popover-foreground">{userName}</div>
                        <div>{userOfficeName}</div>
                      </div>
                      <button
                        onClick={() => {
                          setIsProgramMenuOpen(false)
                          navigate('/settings/office')
                        }}
                        className="w-full text-left block px-2 py-1.5 text-sm text-popover-foreground hover:bg-accent hover:text-accent-foreground rounded transition-colors"
                      >
                        Office Settings
                      </button>
                      <button
                        onClick={() => {
                          setIsProgramMenuOpen(false)
                          navigate('/settings/user')
                        }}
                        className="w-full text-left block px-2 py-1.5 text-sm text-popover-foreground hover:bg-accent hover:text-accent-foreground rounded transition-colors"
                      >
                        User Settings
                      </button>
                      <button
                        onClick={() => {
                          setIsProgramMenuOpen(false)
                          onLogout()
                        }}
                        className="w-full text-left block px-2 py-1.5 text-sm text-destructive hover:bg-accent rounded transition-colors"
                      >
                        Logout
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

          </div>
        )}

        {/* Right section: User menu (hidden when in program on smaller screens; account is in program dropdown) */}
        <div
          className="relative flex-shrink-0"
          ref={userMenuRef}
        >
          <button
            type="button"
            onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
            className="flex items-center gap-3 px-4 py-2 rounded-lg hover:bg-muted transition-colors"
          >
            <div className="text-right hidden sm:block">
              <div className="text-sm font-semibold text-foreground">
                {userName}
              </div>
              <div className="text-xs text-muted-foreground">
                {userOfficeName}
              </div>
            </div>
            <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`} />
          </button>

          {isUserMenuOpen && (
            <div className="absolute top-full right-0 mt-1 w-56 bg-popover border border-border rounded-md shadow-lg z-50">
              <div className="py-1">
                <button
                  onClick={() => {
                    setIsUserMenuOpen(false)
                    navigate('/settings/office')
                  }}
                  className="w-full text-left block px-4 py-2 text-sm text-popover-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                >
                  Office Settings
                </button>
                <button
                  onClick={() => {
                    setIsUserMenuOpen(false)
                    navigate('/settings/user')
                  }}
                  className="w-full text-left block px-4 py-2 text-sm text-popover-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                >
                  User Settings
                </button>
                {user?.is_superuser && (
                  <>
                    <div className="border-t border-border my-1"></div>
                    <button
                      onClick={() => {
                        setIsUserMenuOpen(false)
                        navigate('/console')
                      }}
                      className="w-full text-left flex items-center gap-2 px-4 py-2 text-sm text-popover-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                    >
                      <Shield className="w-4 h-4 text-primary" />
                      Admin Console
                    </button>
                  </>
                )}
                <div className="border-t border-border my-1"></div>
                <button
                  onClick={() => {
                    setIsUserMenuOpen(false)
                    onLogout()
                  }}
                  className="w-full text-left block px-4 py-2 text-sm text-destructive hover:bg-accent transition-colors"
                >
                  Logout
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </nav>
  )
}

export default UnifiedTopNav
